import type { ReactElement } from 'react';
import type { PaymentMark } from '@tenders/shared';

/**
 * Official card-scheme / processor marks as INLINE SVG, for the strip next to
 * the pay button on a Stripe deployment (Kazakhstan; owner, 2026-10-15: «лого
 * официальные должны быть: Visa, Mastercard и Stripe»). Inline rather than
 * `<img src>` so they ship with the bundle — no extra request on the page that
 * sells — and so the brand colours below are the only thing that can change
 * how they look.
 *
 * Sources (paths copied verbatim, only re-wrapped):
 * - Visa — the 2021 Visa wordmark in Visa Blue #1434CB (Visa brand standards),
 *   the same path as `public/img/visa.svg` (commit 725d38fe5).
 * - Mastercard — the 2019 symbol: red #EB001B and yellow #F79E1B circles with
 *   the #FF5F00 overlap (Mastercard brand mark guidelines), the same geometry
 *   as `public/img/mastercard.svg`.
 * - Stripe — the Stripe wordmark in Stripe blurple #635BFF, Stripe's own
 *   Illustrator export as published on Wikimedia Commons
 *   («Stripe Logo, revised 2016.svg»; fetched 2026-10-15). Simple Icons only
 *   carries Stripe's «S» glyph, not the wordmark.
 *
 * Each mark sits in a small white bordered tile — the way checkouts show card
 * schemes — and carries its brand name as the accessible name (`role="img"` +
 * `aria-label`), so a screen reader hears «Visa, Mastercard, Stripe».
 */

const TILE =
  'inline-flex h-[22px] shrink-0 items-center justify-center rounded border border-edge bg-white px-1.5';

function VisaLogo(): ReactElement {
  return (
    <svg
      viewBox="0 0 1000 324.68"
      className="block h-[9px] w-auto"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#1434CB"
        d="m651.19.5c-70.93,0-134.32,36.77-134.32,104.69,0,77.9,112.42,83.28,112.42,122.42,0,16.48-18.88,31.23-51.14,31.23-45.77,0-79.98-20.61-79.98-20.61l-14.64,68.55s39.41,17.41,91.73,17.41c77.55,0,138.58-38.57,138.58-107.66,0-82.32-112.89-87.54-112.89-123.86,0-12.91,15.5-27.05,47.66-27.05,36.29,0,65.89,14.99,65.89,14.99l14.33-66.2S696.61.5,651.18.5h0ZM2.22,5.5L.5,15.49s29.84,5.46,56.72,16.36c34.61,12.49,37.07,19.77,42.9,42.35l63.51,244.83h85.14L379.93,5.5h-84.94l-84.28,213.17-34.39-180.7c-3.15-20.68-19.13-32.48-38.68-32.48,0,0-135.41,0-135.41,0Zm411.87,0l-66.63,313.53h81L494.85,5.5h-80.76Zm451.76,0c-19.53,0-29.88,10.46-37.47,28.73l-118.67,284.8h84.94l16.43-47.47h103.48l9.99,47.47h74.95L934.12,5.5h-68.27Zm11.05,84.71l25.18,117.65h-67.45l42.28-117.65h0Z"
      />
    </svg>
  );
}

function MastercardLogo(): ReactElement {
  return (
    <svg
      viewBox="0 0 1000 618"
      className="block h-[14px] w-auto"
      aria-hidden="true"
      focusable="false"
    >
      <path fill="#EB001B" d="m308,0a309,309 0 1,0 2,0z" />
      <path fill="#F79E1B" d="m690,0a309,309 0 1,0 2,0z" />
      <path fill="#FF5F00" d="m500,66a309,309 0 0,0 0,486 309,309 0 0,0 0-486" />
    </svg>
  );
}

function StripeLogo(): ReactElement {
  return (
    <svg
      viewBox="54 36 360.02 149.84"
      className="block h-[13px] w-auto"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="#635BFF" fillRule="evenodd" clipRule="evenodd">
        <path d="M414,113.4c0-25.6-12.4-45.8-36.1-45.8c-23.8,0-38.2,20.2-38.2,45.6c0,30.1,17,45.3,41.4,45.3c11.9,0,20.9-2.7,27.7-6.5v-20c-6.8,3.4-14.6,5.5-24.5,5.5c-9.7,0-18.3-3.4-19.4-15.2h48.9C413.8,121,414,115.8,414,113.4z M364.6,103.9c0-11.3,6.9-16,13.2-16c6.1,0,12.6,4.7,12.6,16H364.6z" />
        <path d="M301.1,67.6c-9.8,0-16.1,4.6-19.6,7.8l-1.3-6.2h-22v116.6l25-5.3l0.1-28.3c3.6,2.6,8.9,6.3,17.7,6.3c17.9,0,34.2-14.4,34.2-46.1C335.1,83.4,318.6,67.6,301.1,67.6z M295.1,136.5c-5.9,0-9.4-2.1-11.8-4.7l-0.1-37.1c2.6-2.9,6.2-4.9,11.9-4.9c9.1,0,15.4,10.2,15.4,23.3C310.5,126.5,304.3,136.5,295.1,136.5z" />
        <polygon points="223.8,61.7 248.9,56.3 248.9,36 223.8,41.3" />
        <rect x="223.8" y="69.3" width="25.1" height="87.5" />
        <path d="M196.9,76.7l-1.6-7.4h-21.6v87.5h25V97.5c5.9-7.7,15.9-6.3,19-5.2v-23C214.5,68.1,202.8,65.9,196.9,76.7z" />
        <path d="M146.9,47.6l-24.4,5.2l-0.1,80.1c0,14.8,11.1,25.7,25.9,25.7c8.2,0,14.2-1.5,17.5-3.3V135c-3.2,1.3-19,5.9-19-8.9V90.6h19V69.3h-19L146.9,47.6z" />
        <path d="M79.3,94.7c0-3.9,3.2-5.4,8.5-5.4c7.6,0,17.2,2.3,24.8,6.4V72.2c-8.3-3.3-16.5-4.6-24.8-4.6C67.5,67.6,54,78.2,54,95.9c0,27.6,38,23.2,38,35.1c0,4.6-4,6.1-9.6,6.1c-8.3,0-18.9-3.4-27.3-8v23.8c9.3,4,18.7,5.7,27.3,5.7c20.8,0,35.1-10.3,35.1-28.2C117.4,100.6,79.3,105.9,79.3,94.7z" />
      </g>
    </svg>
  );
}

/** The marks that have an official logo here, with the name each is announced by. */
const LOGOS: Partial<Record<PaymentMark, { name: string; Logo: () => ReactElement }>> = {
  visa: { name: 'Visa', Logo: VisaLogo },
  mastercard: { name: 'Mastercard', Logo: MastercardLogo },
  stripe: { name: 'Stripe', Logo: StripeLogo },
};

/**
 * One tile per mark, in the order given. A mark with no logo here falls back to
 * its name as quiet text, so a config that lists a new mark never renders an
 * empty tile.
 */
export function PaymentMarkLogos({ marks }: { marks: readonly PaymentMark[] }): ReactElement {
  return (
    <span data-testid="payment-marks" className="inline-flex items-center gap-1.5">
      {marks.map((mark) => {
        const logo = LOGOS[mark];
        if (!logo) {
          return (
            <span
              key={mark}
              className={`${TILE} text-[10.5px] font-bold leading-none tracking-[0.02em] text-ink-soft`}
            >
              {mark}
            </span>
          );
        }
        const { name, Logo } = logo;
        return (
          <span
            key={mark}
            role="img"
            aria-label={name}
            title={name}
            data-mark={mark}
            className={TILE}
          >
            <Logo />
          </span>
        );
      })}
    </span>
  );
}
